import { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  Column,
  DatePicker,
  InputText,
  InputTextarea,
  ModalForm,
  Row,
  Select,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { escapeHtml, parseHours } from "@components/ow";
import { isoToDate, localIsoDate } from "@helpers/localDate";
import { focusFirstError } from "../../Common/FormPage";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// $centralize centraliza o calendário no ancestral posicionado, e o Column da lib é
// relative: sem position, ele centraliza pela linha toda (a largura do modal).
const StaticColumn = styled(Column)`
  position: static;
`;

const FIELDS = ["bank-event-hours", "bank-event-date", "bank-event-justification"];

// O ajuste manual vai como duas opções (crédito e débito) no lugar do campo de
// direção à parte, para o modal caber numa tela de notebook. `sign` é o efeito no banco.
const TYPE_OPTIONS = [
  { value: "PAYMENT", eventType: "PAYMENT", sign: -1, label: "Pagamento de saldo", hint: "Horas pagas na folha: tira do banco" },
  { value: "DISCOUNT", eventType: "DISCOUNT", sign: 1, label: "Desconto de saldo", hint: "Horas descontadas na folha: devolve ao banco" },
  { value: "ADJUSTMENT_CREDIT", eventType: "ADJUSTMENT", sign: 1, label: "Ajuste manual: crédito", hint: "Soma horas ao banco" },
  { value: "ADJUSTMENT_DEBIT", eventType: "ADJUSTMENT", sign: -1, label: "Ajuste manual: débito", hint: "Tira horas do banco, como na zeragem de saldo positivo" },
];

const OptionText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/** Menu com o rótulo e a linha de apoio; no campo, só o rótulo. */
function labelWithHint(option, { context }) {
  if (context === "value") return option.label;
  return (
    <OptionText>
      <Text $text={option.label} $variant="paragraph_small_medium" $color="N900" />
      <Text $text={option.hint} $variant="caption_medium" $color="N400" />
    </OptionText>
  );
}

function validate(values) {
  const errors = {};
  const minutes = parseHours(values.hours);
  if (minutes === null || minutes <= 0) errors["bank-event-hours"] = "Informe as horas";
  if (!values.date) errors["bank-event-date"] = "Informe a data do acerto";
  if (!values.justification.trim()) errors["bank-event-justification"] = "Informe a justificativa";
  return errors;
}

/**
 * Lançar um acerto de banco de horas (só ADM) no contrato CLT escolhido no espelho.
 * O efeito vai assinado: pagamento tira do banco, desconto devolve e o ajuste manual
 * soma (crédito) ou tira (débito).
 *
 * Props: contractId, employeeName, onClose, onSaved.
 */
export default function BankEventModal({ contractId, employeeName, onClose, onSaved }) {
  const timeClockService = useService("timeClock");
  const [values, setValues] = useState({ type: "PAYMENT", hours: "", date: localIsoDate(), justification: "" });
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const errors = submitted ? validate(values) : {};
  const set = (field, value) => setValues((prev) => ({ ...prev, [field]: value }));

  function close() {
    if (!saving) onClose();
  }

  async function save() {
    setSubmitted(true);
    const found = validate(values);
    if (Object.keys(found).length) {
      focusFirstError(FIELDS.filter((id) => found[id]));
      return;
    }

    // O back guarda o decimal das horas com duas casas
    const hours = Math.round((parseHours(values.hours) / 60) * 100) / 100;
    const type = TYPE_OPTIONS.find((option) => option.value === values.type);

    setSaving(true);
    const result = await timeClockService.createBankEvent({
      contract_id: parseInt(contractId),
      event_type: type.eventType,
      hours_delta: type.sign * hours,
      event_date: values.date,
      justification: values.justification.trim(),
    });
    setSaving(false);
    // Em erro, o sendError do service já mostrou o toast
    if (!result || result.error) return;
    toast.success("Acerto registrado!");
    onSaved();
  }

  return (
    <ModalForm
      $showModal
      $setShowModal={close}
      $noClose={saving}
      $isLoading={saving}
      $size="medium"
      $title={`Lançar acerto · ${escapeHtml(employeeName || "Colaborador")}`}
      $contentBackground="N0"
      $actions={[
        { text: "Cancelar", action: close, variant: "outline", isDisabled: saving },
        { text: "Lançar acerto", action: save, variant: "primary", isDisabled: saving },
      ]}>
      <Stack>
        <Select
          $idForm="bank-event-type"
          $label="Tipo de acerto"
          $isRequired
          $options={TYPE_OPTIONS}
          formatOptionLabel={labelWithHint}
          value={TYPE_OPTIONS.find((option) => option.value === values.type)}
          onChange={(option) => option && set("type", option.value)}
        />
        <Row $gap={20}>
          <Column $columns={6}>
            <InputText
              $idForm="bank-event-hours"
              $label="Horas"
              $variant="default"
              $isRequired
              $hasTooltip="Pode digitar 1h30, 1:30 ou 1,5."
              $errors={errors["bank-event-hours"]}
              placeholder="Ex.: 1h30"
              value={values.hours}
              onChange={(e) => set("hours", e.target.value)}
            />
          </Column>
          <StaticColumn $columns={6}>
            <DatePicker
              $idForm="bank-event-date"
              $centralize
              $label="Data do acerto"
              $variant="default"
              $isRequired
              $errors={errors["bank-event-date"]}
              $date={isoToDate(values.date)}
              $setDate={(date) => set("date", localIsoDate(date) || "")}
            />
          </StaticColumn>
        </Row>
        <InputTextarea
          $idForm="bank-event-justification"
          $label="Justificativa"
          $variant="default"
          $isRequired
          $errors={errors["bank-event-justification"]}
          placeholder="Ex.: 4h extras pagas na folha de junho/2026"
          rows={2}
          value={values.justification}
          onChange={(e) => set("justification", e.target.value)}
        />
      </Stack>
    </ModalForm>
  );
}
